/**
 * `<assistant-questions>` - the question card `ask_user` ends a turn with.
 *
 * The Assistant used to ask what it could not settle in numbered prose, and the
 * person typed the answers back. This draws the same questions as controls,
 * each already on the Assistant's suggestion, so a right guess is one click on
 * Continue. A signal question offers the Assistant's grounded candidates first
 * and the ship's whole signal list behind "Other signal…"; what the Assistant
 * settled alone sits on one "Assumed" line with a Change link.
 *
 * Continuing fires `questions-answer` with the message the answers become (see
 * `questions.ts`); the panel sends it like any other. Once the chat has moved
 * on the card folds to one line - answered here, or in chat instead - and a
 * chat reopened from history draws it the same way, because that state is read
 * from the next turn rather than stored on the card.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { getColumns } from '../../api/endpoints.ts';
import type { ColumnInfo } from '../../api/types.ts';
import { allColumns, selectedShip } from '../../state/store.ts';
import { cacheShipColumns, cachedShipColumns } from '../ship/ship-columns.ts';
import '../../ui/column-combobox.ts';
import {
  answerMessage,
  displayValue,
  initialValues,
  isEmpty,
  sameValue,
  type AnswerValue,
  type Question,
  type QuestionsPayload,
  type QuestionsStatus,
} from './questions.ts';

export interface QuestionsAnswerDetail {
  content: string;
  answers: ReturnType<typeof answerMessage>['answers'];
}

@customElement('assistant-questions')
export class AssistantQuestions extends LightElement {
  @property({ attribute: false }) payload: QuestionsPayload | null = null;
  @property() status: QuestionsStatus = 'open';

  @state() private values: Record<string, AnswerValue> = {};
  /** Questions with "Other signal…" open. */
  @state() private searching = new Set<string>();
  /** Assumed lines opened with Change. */
  @state() private changing = new Set<string>();
  @state() private columnsTick = 0;

  private seededFor: string | null = null;

  override willUpdate(): void {
    if (this.payload && this.seededFor !== this.payload.id) {
      this.seededFor = this.payload.id;
      this.values = initialValues(this.payload);
      this.searching = new Set();
      this.changing = new Set();
    }
  }

  private set(id: string, value: AnswerValue): void {
    this.values = { ...this.values, [id]: value };
  }

  private toggle(q: Question, value: string): void {
    if (!q.multiple) {
      this.set(q.id, value);
      return;
    }
    const current = Array.isArray(this.values[q.id]) ? (this.values[q.id] as string[]) : [];
    this.set(q.id, current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
  }

  private picked(q: Question, value: string): boolean {
    const current = this.values[q.id];
    return Array.isArray(current) ? current.includes(value) : current === value;
  }

  private ready(): boolean {
    return !!this.payload?.questions.every((q) => q.kind === 'text' || !isEmpty(this.values[q.id]));
  }

  private submit(): void {
    if (!this.payload || this.status !== 'open' || !this.ready()) return;
    const message = answerMessage(this.payload, this.values);
    this.dispatchEvent(
      new CustomEvent<QuestionsAnswerDetail>('questions-answer', {
        detail: message,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.submit();
    }
  }

  /** The ship's signals for "Other signal…", fetched once and cached. */
  private columnsFor(shipId: string): ColumnInfo[] | null {
    if (shipId === selectedShip.value && allColumns.value.length) return allColumns.value;
    const cached = cachedShipColumns(shipId);
    if (cached) return cached;
    void getColumns(shipId)
      .then((cols) => cacheShipColumns(shipId, cols))
      .catch(() => cacheShipColumns(shipId, []))
      .finally(() => {
        this.columnsTick += 1;
      });
    return null;
  }

  /* ── Render ───────────────────────────────────────────── */

  private renderOption(q: Question, value: string, note: string | undefined, tag: string | null): TemplateResult {
    const on = this.picked(q, value);
    return html`<button
      class="aq-opt ${on ? 'is-on' : ''}"
      type="button"
      role=${q.multiple ? 'checkbox' : 'radio'}
      aria-checked=${on ? 'true' : 'false'}
      @click=${() => this.toggle(q, value)}
    >
      <span class="aq-mark ${q.multiple ? 'is-box' : ''}" aria-hidden="true"></span>
      <span class="aq-opt-text">
        <span class=${q.kind === 'signal' ? 'aq-signal' : 'aq-choice'}>${value}</span>
        ${note || tag
          ? html`<span class="aq-opt-note"
              >${note ?? ''}${tag ? html`<span class="aq-tag">${tag}</span>` : nothing}</span
            >`
          : nothing}
      </span>
    </button>`;
  }

  private renderOther(q: Question): TemplateResult {
    const offered = new Set((q.options ?? []).map((o) => o.value));
    const current = this.values[q.id];
    const own = (Array.isArray(current) ? current : [current]).filter(
      (v): v is string => typeof v === 'string' && !!v && !offered.has(v),
    );
    const ownRows = own.map((v) => this.renderOption(q, v, 'picked from all signals', 'yours'));
    if (!this.searching.has(q.id)) {
      return html`${ownRows}<button class="aq-link" type="button"
          @click=${() => { this.searching = new Set(this.searching).add(q.id); }}>
          Other signal…
        </button>`;
    }
    const close = (): void => {
      const next = new Set(this.searching);
      next.delete(q.id);
      this.searching = next;
    };
    const pick = (name: string): void => {
      const trimmed = name.trim();
      if (!trimmed) return;
      if (q.multiple) {
        const list = Array.isArray(current) ? current : [];
        if (!list.includes(trimmed)) this.set(q.id, [...list, trimmed]);
      } else {
        this.set(q.id, trimmed);
      }
      close();
    };
    if (!q.ship_id) {
      // No ship to search (an Argus scope can span a fleet): the exact name,
      // typed. The Assistant checks it like anything else it is told.
      return html`${ownRows}<input
        class="aq-input aq-input-wide"
        placeholder="Exact series name"
        aria-label="Exact series name"
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            pick((e.target as HTMLInputElement).value);
          } else if (e.key === 'Escape') close();
        }}
        @blur=${(e: Event) => pick((e.target as HTMLInputElement).value)}
      />`;
    }
    void this.columnsTick;
    const columns = this.columnsFor(q.ship_id);
    return html`${ownRows}${columns === null
      ? html`<span class="aq-note">Loading ${q.ship_id}'s signals…</span>`
      : html`<column-combobox
          .columns=${columns}
          .value=${''}
          .clearOnPick=${true}
          placeholder=${`Search ${q.ship_id} signals…`}
          .select=${pick}
        ></column-combobox>`}`;
  }

  private renderControl(q: Question): TemplateResult {
    if (q.kind === 'choice' || q.kind === 'signal') {
      return html`<div class="aq-options" role=${q.multiple ? 'group' : 'radiogroup'} aria-label=${q.label}>
        ${(q.options ?? []).map((o) =>
          this.renderOption(
            q,
            o.value,
            o.note,
            (Array.isArray(q.suggested) ? q.suggested.includes(o.value) : q.suggested === o.value)
              ? 'suggested'
              : null,
          ),
        )}
        ${q.kind === 'signal' ? this.renderOther(q) : nothing}
      </div>`;
    }
    const value = this.values[q.id];
    if (q.kind === 'number') {
      const suggested = q.suggested;
      return html`<div class="aq-number">
        <span class="aq-number-box">
          <input
            class="aq-input"
            type="number"
            step="any"
            aria-label=${q.label}
            .value=${String(value ?? '')}
            @input=${(e: Event) => this.set(q.id, (e.target as HTMLInputElement).value)}
          />
          ${q.unit ? html`<span class="aq-unit">${q.unit}</span>` : nothing}
        </span>
        ${suggested === undefined || suggested === null
          ? nothing
          : sameValue(value, suggested)
            ? html`<span class="aq-note">Suggested</span>`
            : html`<span class="aq-note"
                >Suggested ${displayValue(q, suggested)} ·
                <button class="aq-link" type="button" @click=${() => this.set(q.id, suggested)}>use it</button></span
              >`}
      </div>`;
    }
    return html`<input
      class="aq-input aq-input-wide"
      aria-label=${q.label}
      .value=${String(value ?? '')}
      @input=${(e: Event) => this.set(q.id, (e.target as HTMLInputElement).value)}
    />`;
  }

  private renderQuestion(q: Question): TemplateResult {
    return html`<div class="aq-q">
      <div class="aq-label">
        ${q.label}${q.hint ? html` <span class="aq-hint">${q.hint}</span>` : nothing}
      </div>
      ${this.renderControl(q)}
    </div>`;
  }

  private renderAssumed(q: Question): TemplateResult {
    if (this.changing.has(q.id)) return this.renderQuestion(q);
    const value = this.values[q.id];
    return html`<div class="aq-assumed">
      <span>
        Assumed: ${q.label} =
        <span class=${q.kind === 'signal' ? 'aq-signal' : ''}>${displayValue(q, value)}</span>${q.hint
          ? html` <span class="aq-hint">(${q.hint})</span>`
          : nothing}
      </span>
      <button class="aq-link" type="button"
        @click=${() => { this.changing = new Set(this.changing).add(q.id); }}>Change</button>
    </div>`;
  }

  override render(): TemplateResult | typeof nothing {
    const payload = this.payload;
    if (!payload) return nothing;
    const asked = payload.questions.filter((q) => !q.assumed);
    if (this.status === 'answered' || this.status === 'chat') {
      const n = asked.length;
      return html`<div class="aq-done ${this.status === 'chat' ? 'is-chat' : ''}">
        <span class="aq-done-mark" aria-hidden="true">${this.status === 'chat' ? '–' : '✓'}</span>
        ${this.status === 'chat'
          ? 'Answered in chat instead'
          : `${n} question${n === 1 ? '' : 's'} answered`}
      </div>`;
    }
    const assumed = payload.questions.filter((q) => q.assumed);
    const ready = this.ready();
    return html`<div
      class="assistant-plotcard aq-card"
      role="group"
      aria-label=${payload.title}
      @keydown=${(e: KeyboardEvent) => this.onKeydown(e)}
    >
      <div class="aq-head">
        <span class="assistant-plotcard-title">${payload.title}</span>
      </div>
      ${asked.map((q) => this.renderQuestion(q))}
      ${assumed.length ? html`<div class="aq-assumptions">${assumed.map((q) => this.renderAssumed(q))}</div>` : nothing}
      <div class="aq-foot">
        <span class="aq-keys">or reply in chat · <kbd>Ctrl</kbd>+<kbd>Enter</kbd></span>
        <button
          class="assistant-send aq-continue"
          type="button"
          ?disabled=${!ready || this.status !== 'open'}
          title=${ready ? '' : 'Answer every question first'}
          @click=${() => this.submit()}
        >
          Continue
        </button>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'assistant-questions': AssistantQuestions;
  }
}
